import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { Movie } from "@/mocks/movies";
import { getCategoryName } from "@/utils/catalog";

interface HeroSlide {
  movie: Movie;
  backdrop: string;
}

interface HeroCarouselProps {
  movies: HeroSlide[];
}

export default function HeroCarousel({ movies }: HeroCarouselProps) {
  const [active, setActive] = useState(0);
  const touchStart = useRef<number | null>(null);

  useEffect(() => {
    const timer = setInterval(() => {
      setActive((prev) => (prev + 1) % movies.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [movies.length]);

  if (movies.length === 0) return null;

  const go = (dir: number) =>
    setActive((prev) => (prev + dir + movies.length) % movies.length);

  return (
    <section
      className="relative h-[420px] w-full overflow-hidden md:h-[560px]"
      onTouchStart={(e) => {
        touchStart.current = e.touches[0].clientX;
      }}
      onTouchEnd={(e) => {
        if (touchStart.current === null) return;
        const diff = e.changedTouches[0].clientX - touchStart.current;
        if (Math.abs(diff) > 50) go(diff > 0 ? -1 : 1);
        touchStart.current = null;
      }}
    >
      {movies.map((slide, index) => (
        <div
          key={slide.movie.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === active ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
        >
          <img
            src={slide.backdrop}
            alt={`${slide.movie.title}焦点海报`}
            title={`${slide.movie.title} - 电影天堂`}
            className="h-full w-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background-950/95 via-background-950/70 to-background-950/30" />
        </div>
      ))}

      <div className="absolute inset-0">
        <div className="mx-auto flex h-full w-full max-w-[1280px] flex-col justify-end px-4 pb-14 md:px-6 md:pb-20">
          <div key={active} className="max-w-2xl animate-fade-up">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <span className="rounded-md bg-accent-500 px-2 py-0.5 text-xs font-bold text-background-950">
                {movies[active].movie.rating.toFixed(1)} 分
              </span>
              {movies[active].movie.genres.map((g) => (
                <span
                  key={g}
                  className="rounded-md bg-background-100/80 px-2 py-0.5 text-xs text-foreground-100"
                >
                  {getCategoryName(g)}
                </span>
              ))}
            </div>
            <h2 className="text-3xl font-bold text-foreground-50 md:text-5xl">
              {movies[active].movie.title}
            </h2>
            <p className="mt-1 text-sm text-foreground-300 md:text-base">
              {movies[active].movie.enTitle} · {movies[active].movie.year} · {movies[active].movie.region} ·{" "}
              {movies[active].movie.duration} 分钟
            </p>
            <p className="mt-4 line-clamp-3 text-sm leading-relaxed text-foreground-200 md:text-base">
              {movies[active].movie.synopsis}
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${movies[active].movie.id}`}
                className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-background-950 transition hover:bg-accent-400"
              >
                <i className="ri-play-circle-line text-lg" />
                查看详情
              </Link>
              <Link
                to={`/category/${movies[active].movie.genres[0]}`}
                className="flex items-center gap-2 whitespace-nowrap rounded-md border border-foreground-100/30 bg-background-950/40 px-5 py-2.5 text-sm text-foreground-50 transition hover:border-accent-500/70 hover:text-accent-400"
              >
                <i className="ri-list-check text-lg" />
                更多{getCategoryName(movies[active].movie.genres[0])}电影
              </Link>
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => go(-1)}
        aria-label="上一张"
        className="absolute left-3 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-background-950/50 text-foreground-50 transition hover:bg-accent-500 hover:text-background-950 md:flex"
      >
        <i className="ri-arrow-left-s-line text-2xl" />
      </button>
      <button
        type="button"
        onClick={() => go(1)}
        aria-label="下一张"
        className="absolute right-3 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-background-950/50 text-foreground-50 transition hover:bg-accent-500 hover:text-background-950 md:flex"
      >
        <i className="ri-arrow-right-s-line text-2xl" />
      </button>

      <div className="absolute bottom-5 left-1/2 flex -translate-x-1/2 items-center gap-2">
        {movies.map((slide, index) => (
          <button
            key={slide.movie.id}
            type="button"
            aria-label={`切换到第 ${index + 1} 张`}
            onClick={() => setActive(index)}
            className={`h-1.5 rounded-full transition-all ${
              index === active ? "w-6 bg-accent-500" : "w-1.5 bg-foreground-100/50"
            }`}
          />
        ))}
      </div>
    </section>
  );
}